// The two authority pages: the national category page
// (/pediatric-sleep-consultant — the hub of the internal linking structure)
// and the Colorado state page. Same kitchen-table voice, same data shape as
// sleepPages.js; merged into SLEEP_PAGES there. Images come from the curated
// design guidelines list.

export const AUTHORITY_PAGES = {
  "pediatric-sleep-consultant": {
    path: "/pediatric-sleep-consultant",
    meta: {
      title: "Certified Pediatric Sleep Consultant | Virtual & In-Home",
      description:
        "What a pediatric sleep consultant actually does: personalized one-on-one sleep support for newborns, infants and toddlers — virtual nationwide, in-home in Denver. Start with a free sleep assessment.",
    },
    label: "Pediatric sleep consulting",
    crumb: "Pediatric Sleep Consultant",
    h1: ["A pediatric sleep consultant", "for the whole picture."],
    img: "https://images.unsplash.com/photo-1770831208055-f373ff919753?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwYXJlbnQlMjBjb21mb3J0aW5nJTIwYmFieSUyMGluJTIwY3JpYnxlbnwwfHx8fDE3ODc5Mzk2NjN8MA&ixlib=rb-4.1.0&q=85&w=1400",
    imgAlt: "A mother holding her newborn close in warm nursery light — the whole-picture support a pediatric sleep consultant provides",
    hero: [
      "If you went looking for a pediatric sleep consultant, you probably didn't search those words first. You searched the 2 a.m. version: why is my baby waking every two hours. Why does my newborn only sleep on me. Is this a regression. Is any of this normal.",
      "Here's what the title means in practice: someone who looks at your child's whole picture — age, temperament, days, nights, what's already been tried — and helps you understand what's actually driving the hard part. Then stays beside you while you change it.",
      "I'm certified in pediatric sleep consulting — which matters less for the letters than for the promise behind them: real training in child sleep, and support that starts with your child, not a method.",
    ],
    sections: [
      {
        h2: "What a pediatric sleep consultant actually does",
        body: [
          "Not hand you a generic schedule. Not prescribe a method and disappear.",
          "My work starts with discovery: what nights look like, what naps look like, how your child falls asleep, what happens when they wake, what you've tried and what happened when you did. Most sleep struggles aren't one problem — they're a pattern. Finding the part of the pattern that matters is the job.",
          "From there, you get a plan built around your child and your family — and support while you live it.",
        ],
      },
      {
        h2: "Who I work with",
        body: [
          "Families from the expecting months through the toddler years — each stage with its own approach, because what helps a newborn and what helps a two-year-old are genuinely different conversations:",
        ],
        list: [
          "Expecting parents who'd rather understand newborn sleep before they're in it",
          "Newborns (0–3 months) — gentle early foundations, day/night confusion, feeding and sleep rhythms",
          "Babies & infants (4–17 months) — night waking, short naps, the crib transfer, regressions",
          "Toddlers & young children (18+ months) — bedtime resistance, early waking, the hallway encore",
        ],
        links: [
          { href: "/newborn-sleep-consultant", label: "Newborn sleep (0–3 months)" },
          { href: "/baby-sleep-consultant", label: "Baby & infant sleep (4–17 months)" },
          { href: "/toddler-sleep-consultant", label: "Toddler sleep (18+ months)" },
        ],
      },
      {
        h2: "What happens in the complimentary pediatric sleep assessment",
        body: [
          "We talk. You tell me what's been happening — the nights, the naps, what you've already tried — and I ask the questions that narrow it down. You'll leave the conversation knowing what I'd look at first, and what working together could look like.",
          "No perfect sleep log required. No pressure at the end. The decision is yours.",
        ],
      },
      {
        h2: "What support looks like after the plan",
        body: [
          "This is the part most sleep advice leaves out. Real weeks don't follow plans exactly: a nap goes sideways, bedtime falls apart, something that worked on Tuesday doesn't work on Thursday.",
          "When that happens, you're not back at a search engine. You ask me — someone who already knows your child, your routine, and what we've tried — and we adjust together. Having instructions is different from having someone in your corner.",
        ],
      },
      {
        h2: "Personalized guidance vs. generic sleep advice",
        body: [
          "Generic advice isn't wrong, exactly — it's just written for an average child who doesn't exist. It can't account for your baby's temperament, your toddler's newfound opinions, your family's actual evenings, or the thing you tried that half-worked.",
          "A consultant's value is the opposite of generic: understand first, then suggest. Your child doesn't need to sleep like somebody else's baby.",
        ],
      },
      {
        h2: "Working together virtually — nationwide",
        body: [
          "Most of the families I work with are virtual, from all over the country. Sleep happens at home, which makes virtual support a natural fit: we talk one on one, and the guidance reaches you in the same rooms where it's all happening.",
          "It's the full service — the same conversation, the same plan, the same support while you implement.",
        ],
        links: [
          { href: "/virtual-sleep-consultant", label: "How virtual sleep support works" },
        ],
      },
      {
        h2: "In-home support in Denver & Colorado",
        body: [
          "For Denver-area families, selected in-home support is available — where I can see bedtime, the room, and the routine as they actually happen. Colorado families beyond Denver work with me virtually, exactly like families nationwide.",
        ],
        links: [
          { href: "/denver-sleep-consultant", label: "Sleep consulting in Denver" },
          { href: "/denver-in-home-sleep-consultant", label: "In-home support (Denver area)" },
          { href: "/colorado-pediatric-sleep-consultant", label: "Across Colorado" },
        ],
      },
    ],
    faq: [
      {
        q: "What's the difference between a pediatric sleep consultant and a sleep coach?",
        a: "The titles get used interchangeably online. What matters more than the label: look for someone certified, who personalizes rather than prescribing a single method, and who supports you through implementation — not just the plan. That's how I work, whatever you call it.",
      },
      {
        q: "Is working with a consultant the same as sleep training?",
        a: "Sleep training is one possible tool — not the whole job. With newborns especially, my work is foundations and expectations, not conventional sleep training. Whatever your child's age, what I suggest depends on them and on what you're comfortable doing night after night.",
      },
      {
        q: "Do you work with families outside Colorado?",
        a: "Yes — most of the families I support are virtual, from all over the United States. Virtual is the full service, not a lesser version of it.",
      },
      {
        q: "What ages do you work with?",
        a: "Expecting parents, newborns, infants, toddlers and young children. The approach changes with the stage — which is exactly why each stage has its own page here.",
      },
      {
        q: "Does this involve cry-it-out?",
        a: "Not unless you choose it. I don't fit families into a predetermined method — you'll know your options, and the decision is yours.",
      },
    ],
    related: [
      { href: "/newborn-sleep-consultant", label: "Newborn sleep consultant" },
      { href: "/baby-sleep-consultant", label: "Baby sleep consultant" },
      { href: "/toddler-sleep-consultant", label: "Toddler sleep consultant" },
      { href: "/virtual-sleep-consultant", label: "Virtual sleep consultant" },
      { href: "/colorado-pediatric-sleep-consultant", label: "Colorado pediatric sleep consultant" },
      { href: "/denver-sleep-consultant", label: "Denver sleep consultant" },
      { href: "/sleep-guides/when-to-hire-sleep-consultant", label: "Guide: when to hire a sleep consultant" },
      { href: "/sleep-guides/sleep-consultant-vs-sleep-coach", label: "Guide: consultant vs. coach" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "colorado-pediatric-sleep-consultant": {
    path: "/colorado-pediatric-sleep-consultant",
    meta: {
      title: "Colorado Pediatric Sleep Consultant | The Nurtured Parent Co.",
      description:
        "A Colorado pediatric sleep consultant for newborn, infant and toddler sleep — in-home support in the Denver area and one-on-one virtual consulting statewide. Start with a free sleep assessment.",
    },
    label: "Colorado · in-home + virtual",
    crumb: "Colorado",
    h1: ["A Colorado pediatric", "sleep consultant."],
    img: "https://images.unsplash.com/photo-1481728236344-b5c828da9edf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxtb3JuaW5nJTIwbnVyc2VyeSUyMGxpZ2h0JTIwYmFieXxlbnwwfHx8fDE3ODc5Mzk2NjN8MA&ixlib=rb-4.1.0&q=85&w=1400",
    imgAlt: "A quiet nursery in morning light — the rooms where Colorado sleep support actually happens",
    hero: [
      "The Nurtured Parent Co. is rooted in Colorado — Denver is home. But the families I work with stretch from the Front Range to the far corners of the state, and well beyond it.",
      "If you're in Colorado, there are two ways we can work together: in-home support in the Denver area, or the same one-on-one virtual support I offer families nationwide.",
    ],
    sections: [
      {
        h2: "In-home support in the Denver area",
        body: [
          "For Denver-area families, selected in-home support is available — bedtime as it actually happens, the room, the light, the routine in real time. Some situations genuinely benefit from someone being there, and if you're local, that's an option we can talk about.",
        ],
        links: [
          { href: "/denver-in-home-sleep-consultant", label: "In-home sleep support (Denver area)" },
          { href: "/denver-sleep-consultant", label: "Sleep consulting in Denver" },
        ],
      },
      {
        h2: "Virtual support, anywhere in Colorado",
        body: [
          "Boulder to Colorado Springs, Fort Collins to the mountain towns and the plains — sleep happens at home, and virtual support meets you there. We talk one on one, build the plan around your child, and I stay alongside you while you put it into practice.",
          "Virtual isn't the long-distance version of the work. For most families — including most Colorado families — it is the service, and it works beautifully.",
        ],
        links: [
          { href: "/virtual-sleep-consultant", label: "How virtual sleep support works" },
        ],
      },
      {
        h2: "Every stage, newborn through toddler",
        body: [
          "The same stage-by-stage approach, wherever in Colorado you are: gentle newborn foundations, infant nights and naps, toddler bedtimes and very early mornings.",
        ],
        links: [
          { href: "/newborn-sleep-consultant", label: "Newborn sleep (0–3 months)" },
          { href: "/baby-sleep-consultant", label: "Baby sleep (4–17 months)" },
          { href: "/toddler-sleep-consultant", label: "Toddler sleep (18+ months)" },
        ],
      },
      {
        h2: "How we'd start",
        body: [
          "With the complimentary pediatric sleep assessment — a real conversation about what's been happening at your house. I'll tell you what I notice, what I'd look at first, and which way of working together fits your family. Then you decide.",
        ],
      },
    ],
    faq: [
      {
        q: "Do you offer in-home visits outside Denver?",
        a: "In-home support is centered on the Denver area. If you're elsewhere in Colorado, virtual is the full service — and the complimentary assessment is a good place to talk through what's possible either way.",
      },
      {
        q: "Is virtual support different for Colorado families?",
        a: "No — it's the same one-on-one work, start to finish. The only difference for Denver-area families is that in-home support exists as an option.",
      },
      {
        q: "What ages do you work with?",
        a: "Expecting parents, newborns, infants, toddlers and young children — with an approach that fits each stage.",
      },
      {
        q: "How is this different from a generic online sleep schedule?",
        a: "A schedule can't see your child, your rooms, or your real evenings — and it can't adjust when Thursday goes sideways. My work starts with understanding what's actually happening in your home and continues while you implement it.",
      },
    ],
    related: [
      { href: "/denver-sleep-consultant", label: "Denver sleep consultant" },
      { href: "/denver-in-home-sleep-consultant", label: "In-home sleep consulting (Denver)" },
      { href: "/virtual-sleep-consultant", label: "Virtual sleep consultant" },
      { href: "/newborn-sleep-consultant", label: "Newborn sleep consultant" },
      { href: "/baby-sleep-consultant", label: "Baby sleep consultant" },
      { href: "/toddler-sleep-consultant", label: "Toddler sleep consultant" },
      { href: "/sleep-guides", label: "Sleep guides — real answers by stage" },
    ],
  },
};
